import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import {
	CampaignViewModel,
	ContactViewModel,
	createRawRichTextContentStateWithText,
	DefaultRawRichTextContentBlockStyleAttribute,
	EmailMessageViewModel,
	excludeKeysOf,
	FinancialBrokerDealer,
	IContact,
	IEmbeddedSocialMediaTemplateContent,
	Industry,
	IOperationResultNoValue,
	IRichContentEditorState,
	ISuggestedSendSchedule,
	ITemplate,
	ITemplateStatus,
	ResourceAutoCompleteViewModelType,
	ScheduleCriteria,
	SocialMediaType,
	TemplateStatus,
	TemplateType,
} from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { parse as parseQueryStringParams } from 'query-string';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import {
	DefaultRichContentRawCSS,
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	ImpersonationContextKey,
	levPlaceholderPopoverCloseCommand,
	levPlaceholderPopoverCommand,
	ModalChildComponentContextKey,
} from '../../../../models';
import {
	CampaignType,
	IAdminUserSessionComponentProps,
	ISystemEmailTemplateWithStatuses,
	ITemplatesIndustryWithCategories,
} from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IEnvironmentComponentProps,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { isIE11 } from '../../../../models/Browser';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { FirstNamePlaceholder, Token } from '../../../../models/Token';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	createRichContentEditorStateWithText,
	getDefaultBulkMessagingBodyEditorState,
	getEntityDisplayName,
	getScheduleTimeOptions,
	getSocialMediaPlainTextContentFromRawRichContent,
	replaceFontFamilyInCss,
	ToolbarFull,
} from '../../../../models/UiUtils';
import {
	TemplateCategoriesViewModel,
	TemplateEditViewModel,
	TemplateWithStatusesViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { AutoCompleteSearchField } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import { Checkbox } from '../../../../web/components/Checkbox';
import { DeprecatedPopover, PopoverType } from '../../../../web/components/DeprecatedPopover';
import { DeprecatedTextArea } from '../../../../web/components/DeprecatedTextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import {
	IRichContentDocumentEditorConfig,
	IRichContentDocumentEditorError,
	RichContentDocumentEditor,
} from '../../../../web/components/richContent/RichContentDocumentEditor';
import { FirstNamePlaceholderInfo } from '../../../../web/components/richContent/RichContentDocumentEditor/FirstNamePlaceholderInfo';
import { ISelectBoxOption } from '../../../../web/components/SelectBox';
import { PreviewSocialMediaPostModal } from '../../../../web/components/socialMedia/PreviewSocialMediaPostModal';
import { SocialMediaPostContentEditor } from '../../../../web/components/socialMedia/SocialMediaPostContentEditor';
import { SocialMediaBannerGraphic } from '../../../../web/components/svgs/graphics/SocialMediaBannerGraphic';
import { SearchIcon } from '../../../../web/components/svgs/icons/SearchIcon';
import { TextInput } from '../../../../web/components/TextInput';
import {
	EditSocialMediaPostContextProvider,
	IEditSocialMediaPostContext,
} from '../../../../web/containers/socialMedia/EditSocialMediaPost/context';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { CitationEditor } from '../CitationEditor';
import { EditCampaignTemplate } from '../EditCampaignTemplate';
import { IEditCampaignTemplateContextProps, withEditCampaignTemplateContext } from '../EditCampaignTemplate/context';
import { styleSheet as editCampaignTemplateStyles } from '../EditCampaignTemplate/styles';
import { IndustryCategorySection } from '../IndustryCategorySection';
import { ExpirationDisplay } from './presentation';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IAdminUserSessionComponentProps,
		IFullscreenModalComponentProps,
		RouteComponentProps<any>,
		IEnvironmentComponentProps,
		IEventLoggingComponentProps,
		IErrorMessageComponentProps,
		IModalContext<ISystemEmailTemplateWithStatuses>,
		INotificationServiceComponentProps<ISystemEmailTemplateWithStatuses>,
		IEditCampaignTemplateContextProps,
		IImpersonationContextComponentProps {
	campaign?: CampaignViewModel;
	className?: string;
	styles?: StyleDeclarationValue[];
}

interface IState {
	bodyEditorHasFocus?: boolean;
	bodyEditorState?: IRichContentEditorState;
	bodyHasText?: boolean;
	campaignExpires?: boolean;
	contentEditorState?: IRichContentEditorState;
	editorLoaded?: boolean;
	featured?: 'all' | 'none' | 'some';
	levPlaceholderPopover?: { x: number; y: number };
	fromSocialTemplate?: boolean;
	initialIndustryOptions?: ITemplateStatus[];
	keywords?: string;
	loadingContent?: boolean;
	postKeywords?: string;
	postSummary?: string;
	readonly?: boolean;
	redirectTo?: string;
	schedule?: ISuggestedSendSchedule;
	selectedExpirationMoment?: moment.Moment;
	selectedIndustryOptions?: ITemplateStatus[];
	showPreview?: boolean;
	showSocialMediaComposer?: boolean;
	subject?: string;
	summary?: string;
	testEmail?: IContact;
	isFirstOption?: boolean;
	templateLink?: string;
	parentTemplateId?: string;
}

const RawRichTextContentBlockStyleAttribute = replaceFontFamilyInCss(
	'arial',
	DefaultRawRichTextContentBlockStyleAttribute
);

class _EditEmailTemplate extends React.Component<IProps, IState> {
	private static DefaultEditorConfig: IRichContentDocumentEditorConfig;
	private mMinutes = 0;
	private campaignEditor: TemplateEditViewModel;
	private mCategoryOptions: ITemplatesIndustryWithCategories[];
	private mEditSocialMediaPostContextRef = React.createRef<IEditSocialMediaPostContext>();

	constructor(props: IProps) {
		super(props);

		makeObservable<
			_EditEmailTemplate,
			| 'mMinutes'
			| 'campaignEditor'
			| 'mCategoryOptions'
			| 'isEditing'
			| 'validSelectedHour'
			| 'timeOptions'
			| 'isFinancialIndustry'
		>(this, {
			mMinutes: observable.ref,
			campaignEditor: observable.ref,
			mCategoryOptions: observable.ref,
			isEditing: computed,
			validSelectedHour: computed,
			timeOptions: computed,
			isFinancialIndustry: computed,
		});

		if (!_EditEmailTemplate.DefaultEditorConfig) {
			_EditEmailTemplate.DefaultEditorConfig = {
				autoresizeToFitContent: true,
				contentHorizontalPadding: 10,
				contentRawCss: replaceFontFamilyInCss('arial', DefaultRichContentRawCSS),
				defaultBlockStyleAttributeStringValue: RawRichTextContentBlockStyleAttribute,
				minHeight: 100,
				plugins: 'placeholders table emoticons autolink',
				toolbar: ToolbarFull,
				toolbarOverflow: 'floating',
				useDefaultEmailPlaceholders: true,
			};
		}
		this.state = {
			contentEditorState: createRichContentEditorStateWithText(''),
			featured: 'none',
			loadingContent: true,
			selectedIndustryOptions: [],
			isFirstOption: false,
			templateLink: '',
			parentTemplateId: '',
		};
		this.campaignEditor = new TemplateEditViewModel(props.userSession);
	}

	public async componentDidMount() {
		const { history, logEvent, logApiError, errorMessages, location: loc } = this.props;

		const locationState: ILocationState<TemplateCategoriesViewModel, TemplateWithStatusesViewModel> =
			history?.location.state;
		const { readonly, fromSocial }: { readonly?: boolean; fromSocial?: boolean } =
			parseQueryStringParams(loc.search) || {};
		this.setState({
			fromSocialTemplate: Boolean(fromSocial),
			readonly: Boolean(readonly),
		});

		try {
			const categories = await this.campaignEditor.loadCategories(
				fromSocial ? CampaignType.Social : CampaignType.Email
			);

			this.mCategoryOptions = categories;

			if (!locationState?.model) {
				this.setState({
					bodyEditorState: getDefaultBulkMessagingBodyEditorState(),
					loadingContent: false,
				});
				return;
			}
			const templateId = locationState.model?.templateReference?.template?.id;

			logEvent('LoadTemplate', { id: templateId });
			await this.campaignEditor.load(templateId);
			this.onContentLoaded();
			this.setState({
				contentEditorState: createRichContentEditorStateWithText(this.campaignEditor?.template?.citation || ''),
			});
		} catch (error) {
			logApiError('LoadTemplate-Error', error);

			errorMessages.pushApiError(error);
		}
	}

	private onContentLoaded = () => {
		const bodyEditorState = convertRawRichTextContentStateToRichContentEditorState(
			this.campaignEditor.template.content
		);
		const selectedIndustryOptions: ITemplateStatus[] = this.campaignEditor.statuses.filter(
			x => x.category !== 'Archived' && x.category !== 'Featured'
		);

		const numberOfFeatured = this.campaignEditor.statuses?.filter(x => x.category === 'Featured')?.length;

		this.mEditSocialMediaPostContextRef.current?.setImages(this.campaignEditor?.socialMedia?.attachments || []);

		this.mEditSocialMediaPostContextRef.current?.setContent(
			getSocialMediaPlainTextContentFromRawRichContent(this.campaignEditor?.socialMedia?.content)
		);

		const selectedExpirationMoment = this.campaignEditor?.template?.schedule?.expirationDate
			? moment(this.campaignEditor.template.schedule.expirationDate)
			: null;

		this.mMinutes = selectedExpirationMoment ? selectedExpirationMoment.minutes() : 0;

		this.props.editCampaignTemplateContext?.setDisplayName(this.campaignEditor.template.name);
		this.setState({
			bodyEditorState,
			bodyHasText: bodyEditorState.hasContent(),
			campaignExpires: this.campaignEditor?.template?.schedule?.expirationDate ? true : false,
			// check if half of the statuses are featured, if so, all "selected" categories are featured
			featured:
				numberOfFeatured === this.campaignEditor.statuses?.length / 2
					? 'all'
					: numberOfFeatured === 0 || !numberOfFeatured
						? 'none'
						: 'some',
			initialIndustryOptions: selectedIndustryOptions,
			keywords: this.campaignEditor.template.keywords?.join(', ') || '',
			loadingContent: false,
			postKeywords: this.campaignEditor?.socialMedia?.keywords?.join(', ') || '',
			postSummary: this.campaignEditor?.socialMedia?.summary,
			schedule: this.campaignEditor.template.schedule,

			selectedExpirationMoment,
			selectedIndustryOptions,
			showPreview: false,
			showSocialMediaComposer: false,
			subject: this.campaignEditor.template.subject,
			summary: this.campaignEditor.template.summary,

			testEmail: null,
		});
	};

	public render() {
		const { className, styles } = this.props;
		const { loadingContent } = this.state;

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} edit-email-template ${className || ''}`}>
				<div className={css(styleSheet.containerInner)}>
					<EditSocialMediaPostContextProvider contextRef={this.mEditSocialMediaPostContextRef}>
						{loadingContent ? <LoadingSpinner type='large' /> : this.renderContent()}
					</EditSocialMediaPostContextProvider>
				</div>
			</div>
		);
	}

	private renderContent() {
		const {
			levPlaceholderPopover,
			subject,
			summary,
			keywords,
			contentEditorState,
			postSummary,
			postKeywords,
			bodyEditorState,
			selectedIndustryOptions,
			featured,
			readonly,
			selectedExpirationMoment,
			campaignExpires,
			showPreview,
			showSocialMediaComposer,
			templateLink,
		} = this.state;
		const titleText = `${readonly ? 'View' : this.isEditing ? 'Edit' : 'Create New'} Campaign`;

		const onToggleSocialMediaContainer = (show: boolean) => () => {
			this.setState({
				showSocialMediaComposer: show,
			});
		};

		return (
			<div>
				{this.isFinancialIndustry && !readonly && !this.isEditing && (
					<div>
						<label htmlFor='template-link-input' className={css(styleSheet.subjectFieldLabel)}>
							Template Link:
						</label>
						<TextInput
							autoComplete='off'
							className={css(styleSheet.fullWidthTextField)}
							inputId='template-link-input'
							onChange={this.onTemplateLinkedChanged}
							type='text'
							value={templateLink || ''}
							placeholder='Copy and paste the Direct link'
						/>
					</div>
				)}
				<EditCampaignTemplate.DisplayName readonly={readonly} titleText={titleText} />
				<div>
					<label htmlFor='create-email-templates-subject-input' className={css(styleSheet.subjectFieldLabel)}>
						Subject:
					</label>
					<TextInput
						autoComplete='off'
						className={css(styleSheet.fullWidthTextField)}
						inputId='create-email-templates-subject-input'
						onChange={this.setInputValue('subject')}
						readOnly={readonly}
						type='text'
						value={subject || ''}
					/>
				</div>
				<div className={css(editCampaignTemplateStyles.subHeader)}>Campaign Content</div>
				<div className={css(styleSheet.bodyField)}>
					<RichContentDocumentEditor
						className={css(styleSheet.bodyEditor)}
						config={_EditEmailTemplate.DefaultEditorConfig}
						contentState={bodyEditorState}
						impersonationContext={this.props.impersonationContext}
						onBlur={this.bodyEditorDidChangeFocus(false)}
						onContentStateChanged={this.onBodyEditorStateChanged}
						onExecuteCommand={this.onEditorExecuteCommand}
						onFocus={this.bodyEditorDidChangeFocus(true)}
						onLoad={this.onEditorLoaded}
						showFirstNameTokenByDefault={true}
						readOnly={readonly}
					/>
					{levPlaceholderPopover ? (
						<DeprecatedPopover
							anchor={
								<span
									style={{
										left: levPlaceholderPopover.x,
										position: 'absolute',
										top: levPlaceholderPopover.y,
									}}
								/>
							}
							dismissOnClickOutside={true}
							isOpen={true}
							preferredPlacement='right'
							onRequestClose={this.onDismissLevPopover}
							type={PopoverType.white}
						>
							<FirstNamePlaceholderInfo onFinishClicked={this.onDismissLevPopover} />
						</DeprecatedPopover>
					) : undefined}
				</div>
				<>
					<div className={css(styleSheet.optionContainer)}>
						<div>
							<Checkbox
								id='feature-campaign-checkbox'
								disabled={readonly}
								onChange={this.onFeaturedChanged}
								partial={featured === 'some'}
								checked={featured === 'all'}
								className={css(styleSheet.featureContainer)}
							>
								<span className={css(styleSheet.feature)}>Feature this campaign</span>
							</Checkbox>
							<ExpirationDisplay
								campaignExpires={campaignExpires}
								selectedExpirationMoment={selectedExpirationMoment}
								setExpirationDay={this.setExpirationDay}
								readonly={readonly}
								onTimeOptionSelected={this.onTimeOptionSelected}
								validSelectedHour={this.validSelectedHour}
								timeOptions={this.timeOptions}
								onExpiryChanged={this.onExpiryChanged}
							/>
						</div>
						{!this.mEditSocialMediaPostContextRef.current?.postContent && !readonly && !this.state.parentTemplateId && (
							<div className={css(styleSheet.socialMediaContainer)}>
								<span className={css(styleSheet.socialMediaPrompt)}>Could this Campaign work on Social media?</span>
								<div className={css(styleSheet.socialMediaBannerContainer)}>
									<button onClick={onToggleSocialMediaContainer(!showSocialMediaComposer)}>
										<SocialMediaBannerGraphic />
										Add Social Media Post!
									</button>
								</div>
							</div>
						)}
					</div>
					{showSocialMediaComposer ||
					(this.mEditSocialMediaPostContextRef.current?.postContent && !this.state.parentTemplateId) ? (
						<>
							<div className={css(styleSheet.sectionLine)} />
							<SocialMediaPostContentEditor
								headerLabel='Create New Social Media Post'
								imagesLabel='Add Media'
								messageLabel='Social Media Content'
								readOnly={readonly}
							/>
							<div className={css(styleSheet.subHeader)}>Post Summary</div>
							<DeprecatedTextArea
								autoComplete='off'
								className={css(styleSheet.summaryTextField, styleSheet.fullWidthTextField)}
								inputId='create-email-templates-summary-social-input'
								readOnly={readonly}
								onChange={this.setInputValue('postSummary')}
								value={postSummary || ''}
							/>
							<TextInput
								autoComplete='off'
								className={css(styleSheet.fullWidthTextField)}
								inputId='create-email-templates-keywords-social-input'
								readOnly={readonly}
								placeholder='Keywords (separated by comma)'
								onChange={this.setInputValue('postKeywords')}
								type='text'
								value={postKeywords || ''}
							/>
							<div className={css(styleSheet.buttonContainer)}>
								<div>
									<button className={css(styleSheet.buttonLink)} onClick={this.onPreviewToggle}>
										Preview
									</button>
								</div>
							</div>
							<PreviewSocialMediaPostModal
								selectedTargets={[
									{ provider: SocialMediaType.Facebook },
									{ provider: SocialMediaType.Instagram },
									{ provider: SocialMediaType.LinkedIn },
								]}
								modalProps={{
									isOpen: showPreview,
									onRequestClose: this.onPreviewToggle,
								}}
								impersonationCtx={this.props.impersonationContext}
								postContent={this.mEditSocialMediaPostContextRef.current?.postContent}
								postImages={this.mEditSocialMediaPostContextRef.current?.postImages}
							/>
						</>
					) : null}
					{!readonly ? (
						<>
							<IndustryCategorySection
								categoryOptions={this.getCategoryOptions()}
								selectedOptions={selectedIndustryOptions}
								onCheckboxChanged={this.onCheckboxChanged}
								onSelectBoxChanged={this.onSelectBoxChanged}
							/>

							<div className={css(styleSheet.sectionLine)} />
							<CitationEditor
								contentEditorState={contentEditorState}
								onContentEditorStateChanged={this.onContentEditorStateChanged}
							/>

							<div className={css(styleSheet.subHeader)}>Campaign Summary</div>
							<DeprecatedTextArea
								autoComplete='off'
								className={css(styleSheet.summaryTextField, styleSheet.fullWidthTextField)}
								inputId='create-email-templates-summary-input'
								readOnly={readonly}
								onChange={this.setInputValue('summary')}
								value={summary || ''}
							/>
							<div className={css(styleSheet.subHeader)}>Keywords</div>
							<TextInput
								autoComplete='off'
								className={css(styleSheet.fullWidthTextField)}
								inputId='create-email-templates-keywords-input'
								placeholder='Keywords (separated by comma)'
								readOnly={readonly}
								onChange={this.setInputValue('keywords')}
								type='text'
								value={keywords || ''}
							/>

							<div className={css(styleSheet.footer)}>
								<div className={css(styleSheet.testSendContainer)}>
									{!this.isEditing && (
										<button
											className={css(baseStyleSheet.ctaButton)}
											disabled={
												this.campaignEditor.isBusy ||
												!(featured === 'none') ||
												(campaignExpires && !selectedExpirationMoment)
											}
											onClick={this.onSave()}
										>
											<span>Save</span>
										</button>
									)}
									<button
										className={css(baseStyleSheet.ctaButtonReverse)}
										disabled={this.campaignEditor.isBusy}
										onClick={this.onSave(true)}
									>
										<span>Save & Publish</span>
									</button>
									{this.campaignEditor.isBusy && <LoadingSpinner type='small' />}
								</div>
								<div className={css(styleSheet.testSendContainer)}>
									<AutoCompleteSearchField
										anchorClassName={css(styleSheet.searchAnchor)}
										className={css(styleSheet.searchBox)}
										clearSearchFieldAfterSelectingItem={false}
										contentPositionY='top'
										inputId='email-recipients-list-search-input'
										inputProps={{ placeholder: 'Select a contact' }}
										leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
										onItemSelected={this.onItemSelected}
										resultsLimit={3}
										type={ResourceAutoCompleteViewModelType.Contact}
									/>
									<button
										className={css(baseStyleSheet.ctaButtonSecondary)}
										disabled={this.campaignEditor.isBusy}
										onClick={this.onSendTestEmail}
									>
										<span>Send Test Email</span>
									</button>
								</div>
							</div>
						</>
					) : null}
				</>
			</div>
		);
	}

	private onItemSelected = (contactModel: IContact) => this.setState({ testEmail: contactModel });

	private onContentEditorStateChanged = (contentEditorState: IRichContentEditorState) => {
		this.setState({ contentEditorState });
	};

	private get isEditing() {
		return this.campaignEditor.isLoaded && !this.state.parentTemplateId;
	}

	private setExpirationDay = (day: Date) => {
		const { selectedExpirationMoment } = this.state;
		const nextMoment = moment(day);

		// set the hours and minutes using the existing selected moment
		if (selectedExpirationMoment) {
			nextMoment.hours(selectedExpirationMoment.hours());
			nextMoment.minutes(selectedExpirationMoment.minutes());
		}

		this.setState({ selectedExpirationMoment: nextMoment });
	};

	private onFeaturedChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ featured: e.target.checked ? 'all' : 'none' });
	};

	private onExpiryChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ campaignExpires: e.target.checked });
		if (!e.target.checked) {
			this.setState({ selectedExpirationMoment: undefined });
		}
	};

	private get validSelectedHour() {
		const { selectedExpirationMoment } = this.state;
		const selectedTotalMinutes = selectedExpirationMoment
			? selectedExpirationMoment.get('hours') * 60 + selectedExpirationMoment.get('minutes')
			: null;
		const placeholder: ISelectBoxOption<number> = {
			isDefault: true,
			title: 'Select a time',
			value: -1,
		};
		return (
			(selectedExpirationMoment ? this.timeOptions.find(x => x.value === selectedTotalMinutes) : null) ?? placeholder
		);
	}

	private get timeOptions() {
		const { userSession } = this.props;
		const { selectedExpirationMoment } = this.state;

		// setting observeSendIntervals to false bypasses the business hrs constraints and allows for all times on all days to be shown
		const allOptions = getScheduleTimeOptions(
			this.mMinutes,
			userSession,
			{ observeSendIntervals: false },
			selectedExpirationMoment
		);
		return allOptions;
	}

	private onTimeOptionSelected = (option: ISelectBoxOption<number>) => {
		const { selectedExpirationMoment } = this.state;
		if (selectedExpirationMoment) {
			const nextSelectedMoment = moment(selectedExpirationMoment);

			nextSelectedMoment.set('hours', Math.floor(option.value / 60));
			nextSelectedMoment.set('minutes', option.value % 60);
			this.setState({
				selectedExpirationMoment: nextSelectedMoment,
			});
		}
	};

	private onPreviewToggle = () => {
		this.setState({ showPreview: !this.state.showPreview });
	};

	private onCheckboxChanged = (industry: Industry) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const { selectedIndustryOptions: oldOptions } = this.state;
		if (!e.target.checked) {
			this.setState({
				selectedIndustryOptions: oldOptions?.filter(x => x.industry !== industry),
			});
		}
	};

	private onSelectBoxChanged = (industry: Industry) => (selectedOption: ISelectBoxOption<string>) => {
		const { selectedIndustryOptions: oldOptions } = this.state;
		const selectedIndustryOptions = oldOptions?.filter(x => x.industry !== industry);
		/**
		 * @TODO take a look at where there is an issue with multiple selections
		 */
		// if (!isFirstOption) {
		// 	const newList: ITemplateStatus[] = getCategoriesByName(this.mCategoryOptions, selectedOption.value);
		// 	this.setState({ selectedIndustryOptions: [...oldOptions, ...newList], isFirstOption: true });
		// 	return;
		// }
		selectedIndustryOptions.push({ industry, category: selectedOption.value });
		this.setState({ selectedIndustryOptions });
	};

	private onEditorExecuteCommand = (
		e: React.ChangeEvent<any> & {
			command: string;
			type: string;
			ui?: any;
			value?: any;
		}
	) => {
		if (!e.defaultPrevented) {
			const { errorMessages } = this.props;
			if (e.command === 'levError' && e.value) {
				const value: IRichContentDocumentEditorError = e.value;
				if (value.error) {
					if (e.ui) {
						errorMessages.pushApiError(value.error);
					}
				}
			}

			if (e.command === levPlaceholderPopoverCommand) {
				const mouseEvent = e.value?.sourceEvent as MouseEvent;
				const anchorClient = (e.value?.sourceEvent?.target as HTMLElement)?.getBoundingClientRect();
				const iframeClient = (e.target.iframeElement as HTMLIFrameElement).getBoundingClientRect();
				if (!anchorClient || !iframeClient) {
					return;
				}

				const clientX = iframeClient.x + anchorClient.x + anchorClient.width;
				const clientY = iframeClient.y + anchorClient.y + anchorClient.height / 2;

				this.setState({
					levPlaceholderPopover: isIE11() ? { x: mouseEvent.x, y: mouseEvent.y } : { x: clientX, y: clientY },
				});
			} else if (e.command === levPlaceholderPopoverCloseCommand) {
				this.onDismissLevPopover();
			}
		}
	};

	private onDismissLevPopover = () => {
		this.setState({
			levPlaceholderPopover: null,
		});
	};

	private onEditorLoaded = () => {
		this.setState({
			editorLoaded: true,
		});
	};

	private bodyEditorDidChangeFocus = (hasFocus: boolean) => () => {
		this.setState({
			bodyEditorHasFocus: hasFocus,
		});
	};

	private onBodyEditorStateChanged = (bodyEditorState: IRichContentEditorState) => {
		this.setState({
			bodyEditorState,
			bodyHasText: bodyEditorState.hasContent(),
		});
	};

	private setInputValue = (key: keyof IState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		this.setState({
			[key]: e.target.value,
		});
	};

	private onSendTestEmail = async () => {
		const { errorMessages, logApiError, userSession } = this.props;
		const { testEmail: testContact, bodyEditorState, subject } = this.state;
		if (!testContact) {
			errorMessages.push({
				messages: ['No contact has been provided'],
			});
			return;
		}

		if (!testContact.primaryEmail?.value?.includes('@levitateapp.com')) {
			errorMessages.push({
				messages: ["You can't send test emails outside of the organization (@levitateapp.com)"],
			});
			return;
		}

		try {
			const viewModel = new EmailMessageViewModel(userSession);

			viewModel.contactsToAdd.add(new ContactViewModel(userSession, testContact));
			viewModel.options.sendSingleEmail = true;

			viewModel.content = bodyEditorState.getRawRichTextContent();
			viewModel.subject = subject || 'Empty Subject Line';
			viewModel.options.scheduledSend = {
				criteria: ScheduleCriteria.Immediately,
			};

			await viewModel.send();

			toast.success(`Test email sent to ${getEntityDisplayName(testContact)}`);
		} catch (error) {
			logApiError('SendTestEmail-Error', error);

			errorMessages.pushApiError(error);
		}
	};

	private onSave =
		(publish = false) =>
		async () => {
			if (this.campaignEditor.isBusy) {
				return;
			}
			const {
				bodyEditorState,
				subject,
				selectedIndustryOptions,
				showSocialMediaComposer,
				featured,
				summary,
				keywords,
				contentEditorState,
				postSummary,
				postKeywords,
				initialIndustryOptions,
				selectedExpirationMoment,
				parentTemplateId,
			} = this.state;
			const {
				errorMessages,
				logApiError,
				logInput,
				parentModal,
				postNotification,
				history,
				editCampaignTemplateContext,
			} = this.props;

			const { displayName } = editCampaignTemplateContext;

			const locationState: ILocationState<TemplateCategoriesViewModel, TemplateWithStatusesViewModel> =
				history?.location.state;

			let errorMessage: string = null;
			if (!bodyEditorState?.hasContent() && showSocialMediaComposer === false) {
				errorMessage = 'Template body is empty.';
			} else {
				const content = bodyEditorState.getRawRichTextContent();

				if (content.document.includes('{ { first name } }')) {
					errorMessage = `This template is using the old token format "${FirstNamePlaceholder.symbol}" (and we're just displaying it to look like the new version). Please delete the existing token, and then re-enter ${FirstNamePlaceholder.symbol} to generate a token in the new format.`;

					errorMessages.push({
						messages: [errorMessage],
					});
					return;
				}
				if (!Token.isFirstNameTokenFormattedCorrectlyLoose(content)) {
					errorMessage = `The first name token does not appear to be formatted properly. Please type ${FirstNamePlaceholder.symbol}.`;
				}
			}

			if (!displayName) {
				errorMessage = 'Template display name is empty.';
			}

			if (!selectedIndustryOptions.length) {
				errorMessage = 'You must select at least one industry-category pair';
			}

			if ((featured === 'some' || featured === 'all') && !publish) {
				errorMessage = 'You may not feature a template without publishing it';
			}

			if (errorMessage) {
				errorMessages.push({
					messages: [errorMessage],
				});
				return;
			}

			const getValidKeywords = (keywordsAsString?: string) => {
				return (keywordsAsString || '')
					.split(',')
					.map(k => k.trim())
					.filter(k => !!k);
			};

			const validKeywords = getValidKeywords(keywords);

			let socialMedia: IEmbeddedSocialMediaTemplateContent = this.campaignEditor?.socialMedia
				? {
						...this.campaignEditor.socialMedia,
					}
				: null;
			if (this.mEditSocialMediaPostContextRef.current?.postContent) {
				if (!socialMedia) {
					socialMedia = {};
				}

				socialMedia.attachments = this.mEditSocialMediaPostContextRef.current.postImages;
				socialMedia.content = createRawRichTextContentStateWithText(
					this.mEditSocialMediaPostContextRef.current.postContent
				);
				socialMedia.keywords = getValidKeywords(postKeywords);
				socialMedia.summary = postSummary;
				socialMedia.citation = getSocialMediaPlainTextContentFromRawRichContent(
					contentEditorState.getRawRichTextContent()
				);
			}

			const template: ITemplate = {
				...(this.campaignEditor?.template || {}),
				citation: getSocialMediaPlainTextContentFromRawRichContent(contentEditorState.getRawRichTextContent()),
				content: bodyEditorState.getRawRichTextContent(),
				keywords: validKeywords,
				name: displayName,
				subject,
				summary,
				templateType: TemplateType.Email,
				parentTemplateId: parentTemplateId || null,
			};

			let schedule: ISuggestedSendSchedule = excludeKeysOf(template.schedule || {}, ['expirationDate']);
			if (selectedExpirationMoment) {
				schedule = {
					...schedule,
					expirationDate: selectedExpirationMoment.toISOString(),
				};
			}
			template.schedule = schedule;

			const onFinish = () => {
				const eventName = `${this.isEditing ? 'Edit' : 'Save'}${publish ? 'AndPublish' : ''}Template`;

				logInput(eventName, 'Click', { id: template.id });

				if (locationState?.viewModel) {
					locationState.viewModel.load()?.catch((err: IOperationResultNoValue) => {
						logApiError('RefreshTemplateCategory-Error', err);

						errorMessages.pushApiError(err);
					});
				}

				postNotification({
					info: this.campaignEditor.toJs(),
					topic: Topics.EDIT_SYSTEM_EMAIL_TEMPLATE,
				});

				parentModal?.onRequestClose(null, false);
			};

			if (!this.isEditing) {
				const templateWithStatuses: ISystemEmailTemplateWithStatuses = {
					socialMedia,
					template,
				};

				if (templateWithStatuses.template.parentTemplateId) {
					templateWithStatuses.socialMedia = null;
				}
				const statuses: ITemplateStatus[] = [];

				// generate statuses based on industries/categories/publishing/featured

				selectedIndustryOptions.forEach(o => {
					statuses.push({
						category: o.category,
						industry: o.industry,
						status: publish ? TemplateStatus.Published : TemplateStatus.Draft,
					});
					if (featured === 'all' && publish) {
						statuses.push({
							category: 'Featured',
							industry: o.industry,
							status: TemplateStatus.Published,
						});
					}
				});

				templateWithStatuses.statuses = statuses;
				this.campaignEditor
					.create(templateWithStatuses, CampaignType.Email)
					?.then(onFinish)
					?.catch((err: IOperationResultNoValue) => {
						logApiError('CreateSystemTemplate-Error', err);

						errorMessages.pushApiError(err);
					});
			} else {
				const statusesToRemove: ITemplateStatus[] = [];
				const statusesToAdd: ITemplateStatus[] = [];
				const statusesToUpdate: ITemplateStatus[] = [];
				// if they are editing instead of creating, they must publish
				this.mCategoryOptions.forEach(industry => {
					const startStatusForIndustry = initialIndustryOptions.filter(x => x.industry === industry.industry)[0];

					const endStatusForIndustry = selectedIndustryOptions.filter(x => x.industry === industry.industry)[0];

					if (!startStatusForIndustry && endStatusForIndustry) {
						statusesToAdd.push({
							...endStatusForIndustry,
							status: TemplateStatus.Published,
						});
					} else if (!endStatusForIndustry && startStatusForIndustry) {
						statusesToRemove.push(startStatusForIndustry);
					} else if (startStatusForIndustry?.category !== endStatusForIndustry?.category) {
						statusesToRemove.push(startStatusForIndustry);
						statusesToAdd.push({
							...endStatusForIndustry,
							status: TemplateStatus.Published,
						});
					} else if (
						startStatusForIndustry?.status !== endStatusForIndustry?.status ||
						endStatusForIndustry?.status === TemplateStatus.Draft
					) {
						statusesToUpdate.push({
							...endStatusForIndustry,
							status: TemplateStatus.Published,
						});
					}

					if (!(template?.associatedTemplates?.length > 0)) {
						if (endStatusForIndustry !== undefined) {
							statusesToAdd.push({
								...endStatusForIndustry,
								status: TemplateStatus.Published,
							});
						}
					}

					// if featured checkbox is not changed, leave as is
					if (featured === 'none') {
						statusesToRemove.push({
							category: 'Featured',
							industry: industry.industry,
							status: TemplateStatus.Published,
						});
					} else if (featured === 'all' && endStatusForIndustry) {
						statusesToAdd.push({
							category: 'Featured',
							industry: industry.industry,
							status: TemplateStatus.Published,
						});
					}
				});

				this.campaignEditor
					.edit(
						template,
						statusesToRemove.filter(x => !!x),
						statusesToAdd.filter(x => !!x),
						statusesToUpdate.filter(x => !!x),
						CampaignType.Email,
						template.parentTemplateId ? null : socialMedia
					)
					?.then(onFinish)
					?.catch((err: IOperationResultNoValue) => {
						logApiError('EditSystemTemplate-Error', err);

						errorMessages.pushApiError(err);
					});
			}
		};

	private get isFinancialIndustry() {
		const hashPath = window.location.hash || '';
		const pathSegments = hashPath.split('/');
		const hasFinancialInUrl = pathSegments.some(segment => segment.toLowerCase() === Industry.Financial.toLowerCase());

		return hasFinancialInUrl;
	}

	private extractTemplateIdFromLink(link: string): string {
		try {
			if (!link) return '';
			const match = link.match(/id=([^&]+)/);
			return match ? match[1] : '';
		} catch {
			return '';
		}
	}

	private async loadTemplateFromId(templateId: string) {
		if (!templateId) return;

		try {
			await this.campaignEditor.load(templateId);
			this.onContentLoaded();
			this.setState({
				contentEditorState: createRichContentEditorStateWithText(this.campaignEditor?.template?.citation || ''),
				parentTemplateId: templateId,
				selectedIndustryOptions: [],
			});
		} catch (error) {
			this.props.errorMessages.pushApiError(error);
		}
	}

	private onTemplateLinkedChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const link = e.target.value;
		const templateId = this.extractTemplateIdFromLink(link);

		this.setState({ templateLink: link });

		if (templateId) {
			await this.loadTemplateFromId(templateId);
		}
	};

	private getCategoryOptions() {
		const { parentTemplateId } = this.state;

		// If it's a financial industry with a parent template ID, filter to only show BrokerDealer categories
		if (this.isFinancialIndustry && parentTemplateId) {
			const brokerDealerValues = Object.values(FinancialBrokerDealer);
			return this.mCategoryOptions
				.map(industry => ({
					...industry,
					categoryNames: industry.categoryNames?.filter(cat => brokerDealerValues.includes(cat as any)) || [],
				}))
				.filter(industry => industry.categoryNames?.length > 0);
		}

		return this.mCategoryOptions;
	}
}

const EditEmailTemplateAsObserver = observer(_EditEmailTemplate);
const EditEmailTemplateWithContext = inject(
	FullScreenModalViewModelKey,
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey,
	ModalChildComponentContextKey,
	ImpersonationContextKey
)(EditEmailTemplateAsObserver);
const EditEmailTemplateWithLocalNotificationService = withNotificationService(EditEmailTemplateWithContext);
export const EditEmailTemplate = withRouter(
	withEventLogging(withEditCampaignTemplateContext(EditEmailTemplateWithLocalNotificationService), 'EditEmailTemplate')
);
